import { useEffect, useRef } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import {
  homeContentInnerSx,
  homeHeroGreetingSx,
  homeHeroWelcomeSx,
  providerShellHeroShellSx,
  providerShellHeroSx,
} from '@/features/home/styles/homeStyles'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { runDetached } from '@/shared/lib/runDetachedPromise'
import { useNotify } from '@/shared/toast/ToastProvider'
import { useSessionStore } from '@/stores/useSessionStore'
import { useSurveyHistoryInfinite } from '../api'
import { HistoryErrorState, HistoryPageSkeleton, SurveyHistoryList } from '../components'
import {
  HISTORY_LOAD_ERROR_TOAST,
  HISTORY_PAGE_TITLE,
  HISTORY_SECTION_TITLE,
  HISTORY_SUBTITLE,
} from '../lib/history-copy'
import {
  historyContentInnerSx,
  historyContentShellSx,
  historyPageRootSx,
  historySubtitleSx,
  historyTitleSx,
} from '../styles/historyStyles'

function greetingFirstName(displayName: string | undefined): string {
  const part = displayName?.trim().split(/\s+/)[0]
  return part || 'there'
}

export function HistoryPage() {
  useDocumentTitle(HISTORY_PAGE_TITLE)
  const notify = useNotify()
  const loadErrorToastAtRef = useRef(0)
  const userName = useSessionStore((s) => s.user?.name)
  const firstName = greetingFirstName(userName)

  const historyQuery = useSurveyHistoryInfinite()
  const items = historyQuery.data?.pages.flatMap((page) => page.items) ?? []
  const hasMore = historyQuery.data?.pages.at(-1)?.hasMore ?? false

  useEffect(() => {
    if (!historyQuery.isError) return
    const updatedAt = historyQuery.errorUpdatedAt ?? 0
    if (updatedAt <= loadErrorToastAtRef.current) return
    loadErrorToastAtRef.current = updatedAt
    notify.showToast(HISTORY_LOAD_ERROR_TOAST, 'error')
  }, [historyQuery.isError, historyQuery.errorUpdatedAt, notify])

  const showInitialSkeleton = historyQuery.isPending && !historyQuery.isError

  if (showInitialSkeleton) {
    return <HistoryPageSkeleton />
  }

  return (
    <Box sx={historyPageRootSx} data-testid="provider-history-page">
      <Box sx={providerShellHeroSx}>
        <Box sx={providerShellHeroShellSx}>
          <Box sx={homeContentInnerSx}>
            <Typography component="h1" sx={homeHeroGreetingSx} data-testid="history-greeting">
              {`Hey, ${firstName}!`}
            </Typography>
            <Typography sx={homeHeroWelcomeSx}>Welcome to Brushfire</Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={historyContentShellSx}>
        <Box sx={historyContentInnerSx}>
          <Typography component="h2" sx={historyTitleSx} data-testid="history-heading">
            {HISTORY_SECTION_TITLE}
          </Typography>
          <Typography sx={historySubtitleSx} data-testid="history-subtitle">
            {HISTORY_SUBTITLE}
          </Typography>

          {historyQuery.isError ? (
            <HistoryErrorState
              onRetry={() => {
                runDetached(historyQuery.refetch())
              }}
              isRetrying={historyQuery.isFetching}
            />
          ) : (
            <SurveyHistoryList
              items={items}
              hasMore={hasMore}
              isFetchingNextPage={historyQuery.isFetchingNextPage}
              onLoadMore={() => {
                runDetached(historyQuery.fetchNextPage())
              }}
            />
          )}
        </Box>
      </Box>

    </Box>
  )
}
